import { useForm, zodResolver } from '@mantine/form';
import { omit } from 'lodash';

import { useUpdateDeclarationDataLrn, useUpdatePartialDeclaration } from '@hooks/declaration';
import { isHighValueDeclaration } from '@utils';
import { DeclarationStakeholdersFormValues, declarationStakeholdersSchema } from './validateDeclarationStakeholdersForm';

export const useDeclarationStakeholdersForm = ({ declaration }) => {
  const saveDeclarationDataLRNMutation = useUpdateDeclarationDataLrn();
  const savePartialDataToDeclarationMutation = useUpdatePartialDeclaration();

  const buildFormValues = (d) => ({
    stakeholders: {
      exporter: {
        header: d?.stakeholders?.exporter?.header,
        name: d?.stakeholders?.exporter.name || '',
        identificationNumber: d?.stakeholders?.exporter?.identificationNumber || '',
        address: {
          country: d?.stakeholders?.exporter?.address?.country || '',
          street: d?.stakeholders?.exporter?.address?.street || '',
          postcode: d?.stakeholders?.exporter?.address?.postcode || '',
          city: d?.stakeholders?.exporter?.address?.city || '',
        },
      },
      importer: {
        name: d?.stakeholders?.importer?.name || '',
        identificationNumber: d?.stakeholders?.importer?.identificationNumber || '',
        address: {
          street: d?.stakeholders?.importer?.address?.street || '',
          postcode: d?.stakeholders?.importer?.address?.postcode || '',
          city: d?.stakeholders?.importer?.address?.city || '',
          country: d?.stakeholders?.importer?.address?.country || '',
        },
      },
      consignee: {
        name: d?.stakeholders?.consignee?.name || '',
        identificationNumber: d?.stakeholders?.consignee?.identificationNumber || '',
        address: {
          street: d?.stakeholders?.consignee?.address?.street || '',
          postcode: d?.stakeholders?.consignee?.address?.postcode || '',
          city: d?.stakeholders?.consignee?.address?.city || '',
          country: d?.stakeholders?.consignee?.address?.country || '',
        },
      },
      declarant: {
        name: d?.stakeholders?.declarant?.name || '',
        identificationNumber: d?.stakeholders?.declarant?.identificationNumber || '',
        address: {
          street: d?.stakeholders?.declarant?.address?.street || '',
          postcode: d?.stakeholders?.declarant?.address?.postcode || '',
          city: d?.stakeholders?.declarant?.address?.city || '',
          country: d?.stakeholders?.declarant?.address?.country || '',
        },
        contactPerson: {
          name: d?.stakeholders?.declarant?.contactPerson?.name || '',
          phoneNumber: d?.stakeholders?.declarant?.contactPerson?.phoneNumber || '',
          email: d?.stakeholders?.declarant?.contactPerson?.email || '',
        },
      },
      intracom: {
        name: d?.stakeholders?.intracom?.name,
        identificationNumber: d?.stakeholders?.intracom?.identificationNumber,
        address: {
          street: d?.stakeholders?.intracom?.address?.street || '',
          postcode: d?.stakeholders?.intracom?.address?.postcode || '',
          city: d?.stakeholders?.intracom?.address?.city || '',
          country: d?.stakeholders?.intracom?.address?.country || '',
        },
      },
      additionalSupplyChainActor: {
        header: d?.stakeholders?.additionalSupplyChainActor?.header,
        name: d?.stakeholders?.additionalSupplyChainActor?.name,
        identificationNumber: d?.stakeholders?.additionalSupplyChainActor?.identificationNumber,
        role: d?.stakeholders?.additionalSupplyChainActor?.role,
        address: {
          street: d?.stakeholders?.additionalSupplyChainActor?.address?.street || '',
          postcode: d?.stakeholders?.additionalSupplyChainActor?.address?.postcode || '',
          city: d?.stakeholders?.additionalSupplyChainActor?.address?.city || '',
          country: d?.stakeholders?.additionalSupplyChainActor?.address?.country || '',
        },
      },
    },
    dv1: {
      seller: {
        header: d?.dv1?.seller?.header,
        name: d?.dv1?.seller?.name || '',
        identificationNumber: d?.dv1?.seller?.identificationNumber || '',
        address: {
          street: d?.dv1?.seller?.address?.street || '',
          city: d?.dv1?.seller?.address?.city || '',
          postcode: d?.dv1?.seller?.address?.postcode || '',
          country: d?.dv1?.seller?.address?.country || '',
        },
      },
      buyer: {
        header: d?.dv1?.buyer?.header,
        identificationNumber: d?.dv1?.buyer?.identificationNumber || '',
        name: d?.dv1?.buyer?.name || '',
        address: {
          street: d?.dv1?.buyer?.address?.street || '',
          city: d?.dv1?.buyer?.address?.city || '',
          postcode: d?.dv1?.buyer?.address?.postcode || '',
          country: d?.dv1?.buyer?.address?.country || '',
        },
      },
    },
  });

  const form = useForm<DeclarationStakeholdersFormValues>({
    initialValues: buildFormValues(declaration),
    validate: zodResolver(declarationStakeholdersSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values) => {
    if (isHighValueDeclaration(declaration.generalInfo.group)) {
      await saveDeclarationDataLRNMutation.mutateAsync({
        lrn: declaration.messageInfo.LRN,
        sequence: declaration.goodShipmentGlobal.sequence,
        partialDeclaration: values,
      });
    } else {
      savePartialDataToDeclarationMutation.mutateAsync({
        id: declaration._id,
        partialDeclaration: omit(values, 'declarationGoods'),
      });
    }
  };

  return { form, isSaving: saveDeclarationDataLRNMutation.isPending || savePartialDataToDeclarationMutation.isPending, onSubmit, buildFormValues };
};
